import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HiPlus } from 'react-icons/hi';
import { Button } from '@/components/common';
import { CreateSupplierRequest, UpdateSupplierRequest, Supplier, SupplierStatus } from '@/types/supplier';
import { categoryApi } from '@/api/endpoints/categories';
import { Category } from '@/types/category';
import { toast } from '@/store/toastStore';

interface SupplierFormProps {
  supplier?: Supplier;
  onSubmit: (data: CreateSupplierRequest | UpdateSupplierRequest) => void;
  onCancel: () => void;
  loading?: boolean;
}

const SupplierForm: React.FC<SupplierFormProps> = ({
  supplier,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation();
  const isEditing = !!supplier;

  const [formData, setFormData] = useState<CreateSupplierRequest>({
    name: '',
    email: '',
    phone: '',
    address: '',
    contactPerson: '',
    taxId: '',
    status: 'active',
    note: '',
    categoryGroupId: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [supplierGroups, setSupplierGroups] = useState<Category[]>([]);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [quickCreateName, setQuickCreateName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);

  // Load supplier groups
  useEffect(() => {
    const loadSupplierGroups = async () => {
      try {
        const response = await categoryApi.getCategories(1, 100, { 
          type: ['supplier_group'], 
          status: ['active'] 
        });
        setSupplierGroups(response.data);
      } catch (error) {
        console.error('Error loading supplier groups:', error);
      }
    };

    loadSupplierGroups();
  }, []);

  // Initialize form data when supplier prop changes
  useEffect(() => {
    if (supplier) {
      setFormData({
        name: supplier.name,
        email: supplier.email,
        phone: supplier.phone,
        address: supplier.address,
        contactPerson: supplier.contactPerson,
        taxId: supplier.taxId || '',
        status: supplier.status,
        note: supplier.note || '',
        categoryGroupId: supplier.categoryGroupId || '',
      });
    }
  }, [supplier]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    }
    // Email is optional; only validate format when provided
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('validation.email');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t('validation.required');
    }
    // Address is optional
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = t('validation.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (isEditing) {
      // For editing, only send the fields that can be updated (no id)
      onSubmit(formData as UpdateSupplierRequest);
    } else {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof CreateSupplierRequest, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleQuickCreateGroup = async () => {
    if (!quickCreateName.trim()) {
      toast.error('Tên nhóm không được để trống');
      return;
    }
    
    setCreatingGroup(true);
    try {
      const newCategory = await categoryApi.createCategory({
        name: quickCreateName.trim(),
        type: 'supplier_group',
        status: 'active',
      });
      
      // Refresh supplier groups list
      const response = await categoryApi.getCategories(1, 100, { 
        type: ['supplier_group'], 
        status: ['active'] 
      });
      setSupplierGroups(response.data);
      
      // Auto-select newly created group
      handleInputChange('categoryGroupId', newCategory.code);
      
      // Reset quick create state
      setQuickCreateName('');
      setShowQuickCreate(false);
      
      toast.success('Tạo nhóm nhà cung cấp thành công');
    } catch (error: any) {
      console.error('Error creating supplier group:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Không thể tạo nhóm nhà cung cấp';
      toast.error(errorMessage);
    } finally {
      setCreatingGroup(false);
    }
  };


  // const statusOptions: { value: SupplierStatus; label: string }[] = [
  //   { value: 'active', label: t('suppliers.status.active') },
  //   { value: 'inactive', label: t('suppliers.status.inactive') },
  // ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* 2-column grid layout with improved styling */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              {t('suppliers.fields.name')} *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('suppliers.placeholders.name')}
              disabled={loading}
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('suppliers.fields.email')}
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('suppliers.placeholders.email')}
              disabled={loading}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
              {t('suppliers.fields.phone')} *
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                errors.phone ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('suppliers.placeholders.phone')}
              disabled={loading}
            />
            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          </div>

          <div>
            <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
              {t('suppliers.fields.contactPerson')} *
            </label>
            <input
              type="text"
              id="contactPerson"
              value={formData.contactPerson}
              onChange={(e) => handleInputChange('contactPerson', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                errors.contactPerson ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('suppliers.placeholders.contactPerson')}
              disabled={loading}
            />
            {errors.contactPerson && <p className="text-red-500 text-xs mt-1">{errors.contactPerson}</p>}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
              {t('suppliers.fields.address')}
            </label>
            <input
              type="text"
              id="address"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                errors.address ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('suppliers.placeholders.address')}
              disabled={loading}
            />
            {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
          </div>

          <div>
            <label htmlFor="taxId" className="block text-sm font-medium text-gray-700 mb-2">
              {t('suppliers.fields.taxId')}
            </label>
            <input
              type="text"
              id="taxId"
              value={formData.taxId}
              onChange={(e) => handleInputChange('taxId', e.target.value)}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${
                errors.taxId ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder={t('suppliers.placeholders.taxId')}
              disabled={loading}
            />
            {errors.taxId && <p className="text-red-500 text-xs mt-1">{errors.taxId}</p>}
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
              {t('suppliers.fields.status')} *
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value as SupplierStatus)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              disabled={loading}
            >
              <option value="active">{t('suppliers.status.active')}</option>
              <option value="inactive">{t('suppliers.status.inactive')}</option>
            </select>
          </div>

          <div>
            <label htmlFor="categoryGroupId" className="block text-sm font-medium text-gray-700 mb-2">
              {t('suppliers.fields.categoryGroup') || 'Nhóm nhà cung cấp'}
            </label>
            <div className="flex items-center space-x-2">
              {!showQuickCreate ? (
                <>
                  <select
                    id="categoryGroupId"
                    value={formData.categoryGroupId || ''}
                    onChange={(e) => handleInputChange('categoryGroupId', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    disabled={loading}
                  >
                    <option value="">-- {t('suppliers.fields.categoryGroup') || 'Chọn nhóm'} --</option>
                    {supplierGroups.map((group) => (
                      <option key={group.code} value={group.code}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowQuickCreate(true)}
                    disabled={loading}
                    title="Tạo nhóm mới"
                    className="px-3 py-2"
                  >
                    <HiPlus className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <div className="flex items-center space-x-2 flex-1">
                  <input
                    type="text"
                    value={quickCreateName}
                    onChange={(e) => setQuickCreateName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleQuickCreateGroup();
                      } else if (e.key === 'Escape') {
                        setShowQuickCreate(false);
                        setQuickCreateName('');
                      }
                    }}
                    placeholder="Nhập tên nhóm..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    autoFocus
                    disabled={creatingGroup}
                  />
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleQuickCreateGroup}
                    loading={creatingGroup}
                    disabled={!quickCreateName.trim() || creatingGroup}
                    className="px-3 py-2"
                  >
                    Lưu
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowQuickCreate(false);
                      setQuickCreateName('');
                    }}
                    disabled={creatingGroup}
                    className="px-3 py-2"
                  >
                    Hủy
                  </Button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Note field - spans full width */}
      <div>
        <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-2">
          {t('suppliers.fields.note')}
        </label>
        <textarea
          id="note"
          rows={4}
          value={formData.note}
          onChange={(e) => handleInputChange('note', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-none"
          placeholder={t('suppliers.placeholders.note')}
          disabled={loading}
        />
      </div>

      {/* Action buttons - positioned bottom-right */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          {t('buttons.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          className="bg-cyan-500 hover:bg-cyan-600 focus:ring-cyan-500"
        >
          {isEditing ? t('buttons.update') : t('buttons.save')}
        </Button>
      </div>
    </form>
  );
};

export default SupplierForm;
